import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { apiFetch } from '../api/api';
import ProductGridItem from '../components/ProductGridItem';
import { mapApiProduct, sortApiProducts } from '../utils/mapProduct';

export default function CategoryProducts() {
  const { categoryId } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const categoryName = location.state?.categoryName ?? '';

  useEffect(() => {
    if (!categoryId) {
      setProducts([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const response = await apiFetch(`/users/products?category_id=${categoryId}`);
        if (!response.ok) throw new Error('products_fetch_failed');
        const json = await response.json();
        const items = sortApiProducts(json.data ?? []).map(mapApiProduct);
        if (!cancelled) setProducts(items);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [categoryId, i18n.language]);

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: 'primary.main' }} />
          <Typography variant="h5" color="primary.main" fontWeight={800}>
            {categoryName || t('products')}
          </Typography>
        </Box>

        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid key={i} size={{ xs: 6, sm: 4, md: 3, lg: 2.4 }}>
                <Skeleton variant="rounded" height={260} />
              </Grid>
            ))}
          </Grid>
        ) : products.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 8 }}>
            {t('products_empty')}
          </Typography>
        ) : (
          <Grid container spacing={2.5}>
            {products.map((product) => (
              <Grid key={product.id} size={{ xs: 6, sm: 4, md: 3, lg: 2.4 }}>
                <ProductGridItem
                  item={product}
                  addToCartLabel={t('add_to_cart')}
                  favouriteLabel={t('favourite_a11y')}
                  discountOffLabel={t('discount_off')}
                  unavailableLabel={t('product_unavailable')}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

    </Box>
  );
}
